import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@stores/authStore';
import { errorMessage, huhokaApi } from './api';
import { isHuhokaAdmin, readAuthorizeRequest } from './huhoka';

const PANEL = 'w-full max-w-[520px] rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] px-7 py-6';
const BUTTON =
  'inline-flex h-[38px] items-center justify-center rounded-lg px-4 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-50';

/**
 * `/integrations/huhoka/authorize` - HuHoKa CRM started the connection and sent
 * the admin's browser here (handshake contract section 2, CentraOne Plus as B).
 * The request is checked server-side before anything is shown; Approve and
 * Cancel each come back with the one redirect integration-hub allows.
 */
export function HuhokaAuthorizePage() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = isHuhokaAdmin(user?.role);
  const request = useMemo(() => readAuthorizeRequest(location.search), [location.search]);
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const info = useQuery({
    queryKey: ['integrations', 'huhoka', 'authorize', location.search],
    queryFn: () => huhokaApi.validate(request),
    enabled: isAdmin,
    retry: false,
    staleTime: Infinity,
  });

  const respond = async (decision: 'approve' | 'deny') => {
    setBusy(decision);
    setActionError(null);
    try {
      const { redirectUrl } = decision === 'approve' ? await huhokaApi.approve(request) : await huhokaApi.deny(request);
      window.location.assign(redirectUrl);
    } catch (err) {
      setActionError(errorMessage(err, 'The request could not be completed. Start again from HuHoKa CRM.'));
      setBusy(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--co-canvas)] p-4">
      <div className={PANEL} style={{ boxShadow: 'var(--co-shadow-card)' }}>
        <p className="co-mono text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
          Connect an app
        </p>

        {!isAdmin ? (
          <>
            <h1 className="mt-2 text-[22px] font-bold leading-tight text-[var(--co-ink)]">Admin approval needed</h1>
            <p className="mt-3 text-[13px] leading-[1.6] text-[var(--co-ink-2)]">
              Only a workspace admin can connect HuHoKa CRM. Ask an admin of this workspace to start the connection
              again from HuHoKa CRM and approve it here.
            </p>
            <Link to="/dashboard" className="mt-5 inline-block text-[13px] text-[var(--co-brass-ink)] hover:underline">
              Back to CentraOne Plus
            </Link>
          </>
        ) : info.isLoading ? (
          <p className="mt-4 text-[13px] text-[var(--co-ink-3)]">Checking the connection request...</p>
        ) : info.isError || !info.data ? (
          <>
            <h1 className="mt-2 text-[22px] font-bold leading-tight text-[var(--co-ink)]">
              This connection request can't be used
            </h1>
            <p className="mt-3 text-[13px] leading-[1.6] text-[var(--co-burgundy)]" role="alert">
              {errorMessage(info.error, 'The request is not valid.')}
            </p>
            <p className="mt-2 text-[12.5px] leading-[1.6] text-[var(--co-ink-3)]">
              Start the connection again from HuHoKa CRM.
            </p>
            <Link to="/dashboard" className="mt-5 inline-block text-[13px] text-[var(--co-brass-ink)] hover:underline">
              Back to CentraOne Plus
            </Link>
          </>
        ) : (
          <>
            <h1 className="mt-2 text-[22px] font-bold leading-tight text-[var(--co-ink)]">
              HuHoKa CRM wants to connect to {info.data.localTenantName}
            </h1>
            {info.data.remoteTenantName && (
              <p className="mt-2 text-[13px] text-[var(--co-ink-2)]">
                HuHoKa CRM organisation: <strong>{info.data.remoteTenantName}</strong>
              </p>
            )}
            <p className="mt-4 text-[12.5px] font-semibold text-[var(--co-ink)]">What will be shared</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]">
              {info.data.sharedData.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-4 text-[12px] leading-[1.6] text-[var(--co-ink-3)]">
              You can disconnect at any time from Integrations. Both products keep their own data.
            </p>

            {actionError && (
              <p className="mt-3 text-[12.5px] text-[var(--co-burgundy)]" role="alert">
                {actionError}
              </p>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => respond('deny')}
                disabled={busy !== null}
                className={`${BUTTON} border border-[var(--co-line-strong)] text-[var(--co-ink-2)]`}
              >
                {busy === 'deny' ? 'Cancelling...' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => respond('approve')}
                disabled={busy !== null}
                className={`${BUTTON} bg-[var(--co-brass)] text-[var(--co-ink-invert)]`}
              >
                {busy === 'approve' ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default HuhokaAuthorizePage;
